import React, { useEffect, useMemo, useState } from 'react';
import { bool, arrayOf, func, number, shape, string } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { injectIntl, intlShape } from '../../util/reactIntl';
import { propTypes } from '../../util/types';
import { types as sdkTypes } from '../../util/sdkLoader';
import { withViewport } from '../../util/uiHelpers';
import { isScrollingDisabled, manageDisableScrolling } from '../../ducks/ui.duck';

import { Heading, Page, LayoutSingleColumn, NamedLink } from '../../components';

import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';
import FooterContainer from '../../containers/FooterContainer/FooterContainer';
import SearchMap from '../SearchPage/SearchMap/SearchMap';
import { userLocation } from '../../util/maps';
import {
  geocodeDealerAddress,
  geocodeDealerAddresses as geocodeDealerAddressesApi,
  geocodeDealerZip,
} from '../../util/api';
import { getStoreImageUrl } from '../../util/sellerImages';
import { loadData } from './DealersDirectoryPage.duck';
import { selectDealers, selectDealersInProgress, selectDealersError } from './DealersDirectoryPage.duck';

import css from './DealersDirectoryPage.module.css';

const { LatLng, LatLngBounds } = sdkTypes;
const noop = () => {};
const MAX_LAZY_GEOCODE_REQUESTS_PER_SESSION = 24;
const METERS_TO_MILES = 0.000621371;
const EARTH_RADIUS_METERS = 6371000;

const toRadians = value => (value * Math.PI) / 180;
const normalizeZipCode = value =>
  (typeof value === 'string' ? value : '')
    .replace(/\D/g, '')
    .slice(0, 5);
const hasCoordinatePair = coordinates =>
  coordinates && typeof coordinates.lat === 'number' && typeof coordinates.lng === 'number';
const hasStructuredAddress = address =>
  !!address?.streetAddress && !!address?.city && !!address?.state;

const getDealerPublicData = dealer => dealer?.attributes?.profile?.publicData || {};
const getUserPublicData = user => user?.attributes?.profile?.publicData || {};
const getDealerStats = dealer => dealer?.attributes?.dealerStats || {};

const getDealerName = dealer => {
  const publicData = getDealerPublicData(dealer);
  return publicData.businessName || dealer?.attributes?.profile?.displayName || 'Dealer';
};

const getDealerImage = dealer => {
  return getStoreImageUrl(dealer) || '/static/footer-logo.png';
};

const getDealerCoordinates = publicData => {
  const origin = publicData?.addressGeo?.origin || publicData?.storedAddress?.origin;
  const lat = origin?.lat ?? publicData?.storedAddress?.lat;
  const lng = origin?.lng ?? publicData?.storedAddress?.lng;

  return hasCoordinatePair({ lat, lng }) ? { lat, lng } : null;
};

const getAddressGeoCoordinates = addressGeo => {
  const origin = addressGeo?.origin;
  return hasCoordinatePair(origin) ? { lat: origin.lat, lng: origin.lng } : null;
};

const getDistanceOriginMessage = (distanceOriginSource, appliedZip) => {
  if (distanceOriginSource === 'saved-address') {
    return 'Distance is calculated from your saved address';
  }

  if (distanceOriginSource === 'user-location') {
    return 'Distance is calculated from your current location';
  }

  if (distanceOriginSource === 'zip' && appliedZip) {
    return `Distance is calculated from ZIP "${appliedZip}"`;
  }

  return null;
};

const getDistanceInMiles = (fromCoordinates, toCoordinates) => {
  if (!fromCoordinates || !toCoordinates) {
    return null;
  }

  const latDelta = toRadians(toCoordinates.lat - fromCoordinates.lat);
  const lngDelta = toRadians(toCoordinates.lng - fromCoordinates.lng);
  const fromLat = toRadians(fromCoordinates.lat);
  const toLat = toRadians(toCoordinates.lat);

  const haversine =
    Math.sin(latDelta / 2) * Math.sin(latDelta / 2) +
    Math.cos(fromLat) * Math.cos(toLat) * Math.sin(lngDelta / 2) * Math.sin(lngDelta / 2);
  const arc = 2 * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine));

  return EARTH_RADIUS_METERS * arc * METERS_TO_MILES;
};

const getDealerReviewSummary = dealer => {
  const reviews = dealer?.attributes?.reviews || [];
  const reviewCount = Array.isArray(reviews) ? reviews.length : 0;
  const totalRating = reviewCount
    ? reviews.reduce((sum, review) => sum + Number(review?.attributes?.rating || 0), 0)
    : 0;

  return {
    reviewCount,
    averageRating: reviewCount ? totalRating / reviewCount : null,
  };
};

const hasDealerReviewsLoaded = dealer => Array.isArray(dealer?.attributes?.reviews);

const getDealerAddressLines = dealer => {
  const publicData = getDealerPublicData(dealer);
  const address = publicData?.address;
  const line1Parts = [address?.streetAddress, address?.unitNumber].filter(Boolean);
  const line2Parts = [
    address?.city,
    address?.state,
    [address?.zip, address?.country && address.country !== 'US' ? address.country : null]
      .filter(Boolean)
      .join(' '),
  ].filter(Boolean);

  if (line1Parts.length || line2Parts.length) {
    return {
      line1: line1Parts.join(', '),
      line2: line2Parts.join(', '),
    };
  }

  const placeName = publicData?.storedAddress?.place_name || '';
  const segments = placeName
    .split(',')
    .map(segment => segment.trim())
    .filter(Boolean);

  return {
    line1: segments[0] || '',
    line2: segments.slice(1, 3).join(', '),
  };
};

const getDealerZip = dealer => {
  const publicData = getDealerPublicData(dealer);
  return normalizeZipCode(publicData?.address?.zip);
};

const buildMapReadyDealer = dealer => {
  const publicData = getDealerPublicData(dealer);
  const coordinates = getDealerCoordinates(publicData);

  if (!coordinates) {
    return null;
  }

  return {
    ...dealer,
    attributes: {
      ...dealer.attributes,
      profile: {
        ...dealer.attributes.profile,
        publicData: {
          ...publicData,
          storedAddress: {
            ...(publicData?.storedAddress || {}),
            lat: coordinates.lat,
            lng: coordinates.lng,
            origin: publicData?.storedAddress?.origin || coordinates,
          },
        },
      },
    },
  };
};

const mergeDealerAddressGeo = (dealer, addressGeo) => {
  if (!addressGeo) {
    return dealer;
  }

  const publicData = getDealerPublicData(dealer);
  return {
    ...dealer,
    attributes: {
      ...dealer.attributes,
      profile: {
        ...dealer.attributes.profile,
        publicData: {
          ...publicData,
          addressGeo,
        },
      },
    },
  };
};

const buildDealerMapListing = dealer => {
  const coordinates = getDealerCoordinates(getDealerPublicData(dealer));
  const dealerStats = getDealerStats(dealer);
  if (!coordinates) {
    return null;
  }

  return {
    id: dealer.id,
    type: 'listing',
    author: { id: dealer.id },
    attributes: {
      geolocation: new LatLng(coordinates.lat, coordinates.lng),
    },
    totalListings: dealerStats.totalListings,
  };
};

const createBoundsFromDealers = dealers => {
  const coordinates = dealers
    .map(dealer => getDealerCoordinates(getDealerPublicData(dealer)))
    .filter(Boolean);

  if (!coordinates.length) {
    return null;
  }

  const lats = coordinates.map(coord => coord.lat);
  const lngs = coordinates.map(coord => coord.lng);
  const northEast = new LatLng(Math.max(...lats), Math.max(...lngs));
  const southWest = new LatLng(Math.min(...lats), Math.min(...lngs));

  return new LatLngBounds(northEast, southWest);
};

const sortDealers = (dealers, sortBy, distanceByDealerId, shouldSortByDistance) => {
  const sortedDealers = [...dealers];

  if (shouldSortByDistance) {
    return sortedDealers.sort((first, second) => {
      const firstDistance = distanceByDealerId[first?.id?.uuid] ?? Number.POSITIVE_INFINITY;
      const secondDistance = distanceByDealerId[second?.id?.uuid] ?? Number.POSITIVE_INFINITY;
      return firstDistance - secondDistance;
    });
  }

  if (sortBy === 'reviews') {
    return sortedDealers.sort((first, second) => {
      const firstReviews = getDealerReviewSummary(first).reviewCount;
      const secondReviews = getDealerReviewSummary(second).reviewCount;
      return secondReviews - firstReviews;
    });
  }

  return sortedDealers.sort((first, second) => getDealerName(first).localeCompare(getDealerName(second)));
};

const DealerCard = props => {
  const { dealer, intl, isActive, onSelect, distanceMiles, isDistancePending } = props;
  const publicData = getDealerPublicData(dealer);
  const { line1, line2 } = getDealerAddressLines(dealer);
  const { reviewCount, averageRating } = getDealerReviewSummary(dealer);
  const { totalListings } = getDealerStats(dealer);
  const hasMapCoordinates = !!getDealerCoordinates(publicData);
  const shouldShowReviews = hasDealerReviewsLoaded(dealer);
  const ratingText =
    averageRating != null
      ? `${intl.formatNumber(averageRating, {
          minimumFractionDigits: 1,
          maximumFractionDigits: 1,
        })} / 5`
      : 'No rating yet';

  return (
    <article className={`${css.dealerCard} ${isActive ? css.dealerCardActive : ''}`}>
      <button type="button" className={css.dealerCardBody} onClick={onSelect}>
        <div className={css.dealerImageContainer}>
          <img src={getDealerImage(dealer)} className={css.dealerImage} alt={getDealerName(dealer)} />
        </div>
        <div className={css.dealerInfo}>
          <div className={css.dealerHeadingRow}>
            <h3 className={css.dealerName}>{getDealerName(dealer)}</h3>
            {hasMapCoordinates ? <span className={css.metaBadge}>Mapped</span> : null}
          </div>
          {shouldShowReviews ? (
            <p className={css.dealerReviews}>
              {ratingText} {reviewCount ? `(${reviewCount} reviews)` : ''}
            </p>
          ) : null}
          {line1 ? <p className={css.dealerAddressLine}>{line1}</p> : null}
          {line2 ? <p className={css.dealerAddressLine}>{line2}</p> : null}
          <div className={css.dealerMetaRow}>
            {typeof totalListings === 'number' ? (
              <span className={css.dealerMetaText}>
                {totalListings} {totalListings === 1 ? 'Listing' : 'Listings'}
              </span>
            ) : null}
          </div>
        </div>
      </button>
      <div className={css.dealerActions}>
        {isDistancePending ? (
          <span className={css.distancePendingLabel}>Calculating distance...</span>
        ) : typeof distanceMiles === 'number' ? (
          <span className={css.distanceLabel}>
            {intl.formatNumber(distanceMiles, {
              minimumFractionDigits: distanceMiles < 10 ? 1 : 0,
              maximumFractionDigits: 1,
            })}{' '}
            mi
          </span>
        ) : null}
        <NamedLink
          name="TeamPage"
          params={{ id: dealer.id.uuid, type: 'detail' }}
          className={css.browseButton}
        >
          Browse
        </NamedLink>
      </div>
    </article>
  );
};

DealerCard.propTypes = {
  dealer: propTypes.user.isRequired,
  distanceMiles: number,
  isDistancePending: bool,
  intl: intlShape.isRequired,
  isActive: bool.isRequired,
  onSelect: func.isRequired,
};

const DealersDirectoryPageComponent = props => {
  const {
    currentUser,
    dealers,
    dealersInProgress,
    dealersError,
    intl,
    location,
    scrollingDisabled,
  } = props;
  const [draftZip, setDraftZip] = useState('');
  const [appliedZip, setAppliedZip] = useState('');
  const [distanceOrigin, setDistanceOrigin] = useState(null);
  const [searchError, setSearchError] = useState(null);
  const [searchInProgress, setSearchInProgress] = useState(false);
  const [showMappedOnly, setShowMappedOnly] = useState(false);
  const [showReviewedOnly, setShowReviewedOnly] = useState(false);
  const [sortBy, setSortBy] = useState('name');
  const [activeDealerId, setActiveDealerId] = useState(null);
  const [geocodedAddressGeoByDealerId, setGeocodedAddressGeoByDealerId] = useState({});
  const [requestedGeocodeIds, setRequestedGeocodeIds] = useState([]);
  const hasDealers = dealers && dealers.length > 0;
  const hasError = dealersError;
  const locationForMap = location || { search: '' };
  const activeSearchOrigin = distanceOrigin?.origin || null;
  const currentUserPublicData = getUserPublicData(currentUser);

  const enrichedDealers = useMemo(
    () =>
      dealers.map(dealer => {
        const dealerId = dealer?.id?.uuid;
        const addressGeo = dealerId ? geocodedAddressGeoByDealerId[dealerId] : null;
        return addressGeo ? mergeDealerAddressGeo(dealer, addressGeo) : dealer;
      }),
    [dealers, geocodedAddressGeoByDealerId]
  );

  const distanceByDealerId = useMemo(() => {
    if (!activeSearchOrigin) {
      return {};
    }

    return enrichedDealers.reduce((accumulator, dealer) => {
      const dealerId = dealer?.id?.uuid;
      const coordinates = getDealerCoordinates(getDealerPublicData(dealer));
      const distanceMiles = getDistanceInMiles(activeSearchOrigin, coordinates);

      if (dealerId && typeof distanceMiles === 'number') {
        accumulator[dealerId] = distanceMiles;
      }

      return accumulator;
    }, {});
  }, [activeSearchOrigin, enrichedDealers]);

  const filteredDealers = useMemo(() => {
    const nextDealers = enrichedDealers
      .filter(dealer => {
        const publicData = getDealerPublicData(dealer);
        const matchesZipFilter = appliedZip ? getDealerZip(dealer) === appliedZip : true;
        const matchesMappedFilter = showMappedOnly ? !!getDealerCoordinates(publicData) : true;
        const matchesReviewFilter = showReviewedOnly
          ? getDealerReviewSummary(dealer).reviewCount > 0
          : true;

        return matchesZipFilter && matchesMappedFilter && matchesReviewFilter;
      });

    const shouldSortByDistance = sortBy === 'distance' && !!activeSearchOrigin;
    return sortDealers(nextDealers, sortBy, distanceByDealerId, shouldSortByDistance);
  }, [
    activeSearchOrigin,
    appliedZip,
    distanceByDealerId,
    enrichedDealers,
    showMappedOnly,
    showReviewedOnly,
    sortBy,
  ]);

  useEffect(() => {
    if (requestedGeocodeIds.length >= MAX_LAZY_GEOCODE_REQUESTS_PER_SESSION) {
      return;
    }

    const candidateIds = filteredDealers
      .filter(dealer => {
        const publicData = getDealerPublicData(dealer);
        const dealerId = dealer?.id?.uuid;
        const hasStructuredAddress =
          !!publicData?.address?.streetAddress && !!publicData?.address?.city && !!publicData?.address?.state;
        const hasCoordinates = !!getDealerCoordinates(publicData);
        const wasRequested = dealerId ? requestedGeocodeIds.includes(dealerId) : true;

        return dealerId && hasStructuredAddress && !hasCoordinates && !wasRequested;
      })
      .slice(0, Math.min(8, MAX_LAZY_GEOCODE_REQUESTS_PER_SESSION - requestedGeocodeIds.length))
      .map(dealer => dealer.id.uuid);

    if (!candidateIds.length) {
      return;
    }

    setRequestedGeocodeIds(previousIds => [...previousIds, ...candidateIds]);

    geocodeDealerAddressesApi({ dealerIds: candidateIds })
      .then(response => {
        const results = response?.results || [];
        const nextAddressGeoByDealerId = results.reduce((accumulator, result) => {
          if (result?.id && result?.addressGeo?.origin) {
            accumulator[result.id] = result.addressGeo;
          }
          return accumulator;
        }, {});

        if (Object.keys(nextAddressGeoByDealerId).length > 0) {
          setGeocodedAddressGeoByDealerId(previousState => ({
            ...previousState,
            ...nextAddressGeoByDealerId,
          }));
        }
      })
      .catch(error => {
        console.error('[DealersDirectoryPage] Lazy geocoding failed:', error);
      });
  }, [filteredDealers, requestedGeocodeIds]);

  const mappedDealers = useMemo(
    () => filteredDealers.map(buildMapReadyDealer).filter(Boolean),
    [filteredDealers]
  );

  const mapListings = useMemo(
    () => mappedDealers.map(buildDealerMapListing).filter(Boolean),
    [mappedDealers]
  );

  const mapBounds = useMemo(() => {
    if (mappedDealers.length > 0) {
      return createBoundsFromDealers(mappedDealers);
    }

    return null;
  }, [mappedDealers]);

  useEffect(() => {
    const activeDealerStillVisible = filteredDealers.some(dealer => dealer?.id?.uuid === activeDealerId);
    if (!activeDealerStillVisible) {
      setActiveDealerId(filteredDealers[0]?.id?.uuid || null);
    }
  }, [activeDealerId, filteredDealers]);

  const activeMapListingId = mappedDealers.some(dealer => dealer?.id?.uuid === activeDealerId)
    ? mappedDealers.find(dealer => dealer?.id?.uuid === activeDealerId)?.id
    : mappedDealers[0]?.id || null;

  const resolveDistanceOrigin = async normalizedZip => {
    const savedAddressOrigin = getAddressGeoCoordinates(currentUserPublicData?.addressGeo);
    if (savedAddressOrigin) {
      return {
        origin: savedAddressOrigin,
        source: 'saved-address',
      };
    }

    if (hasStructuredAddress(currentUserPublicData?.address)) {
      try {
        const response = await geocodeDealerAddress({ address: currentUserPublicData.address });
        const place = response?.place || null;

        if (place?.origin) {
          return {
            origin: place.origin,
            source: 'saved-address',
          };
        }
      } catch (error) {
        console.error('[DealersDirectoryPage] Saved address geocoding failed:', error);
      }
    }

    try {
      const currentLocation = await userLocation();
      if (hasCoordinatePair(currentLocation)) {
        return {
          origin: { lat: currentLocation.lat, lng: currentLocation.lng },
          source: 'user-location',
        };
      }
    } catch (error) {
      console.error('[DealersDirectoryPage] Browser location fallback failed:', error);
    }

    try {
      const response = await geocodeDealerZip({ zip: normalizedZip });
      const place = response?.place || null;

      if (place?.origin) {
        return {
          origin: place.origin,
          source: 'zip',
        };
      }
    } catch (error) {
      console.error('[DealersDirectoryPage] ZIP fallback geocoding failed:', error);
    }

    return null;
  };

  const handleSearchSubmit = async event => {
    event.preventDefault();

    const trimmedZip = draftZip.trim();
    const normalizedZip = normalizeZipCode(trimmedZip);

    if (!trimmedZip) {
      setAppliedZip('');
      setDistanceOrigin(null);
      setSearchError(null);
      if (sortBy === 'distance') {
        setSortBy('name');
      }
      return;
    }

    if (!/^\d{5}$/.test(normalizedZip)) {
      setDistanceOrigin(null);
      setSearchError('Enter a valid 5-digit ZIP code.');
      if (sortBy === 'distance') {
        setSortBy('name');
      }
      return;
    }

    setAppliedZip(normalizedZip);
    setDistanceOrigin(null);
    setSearchInProgress(true);
    setSearchError(null);

    try {
      const nextDistanceOrigin = await resolveDistanceOrigin(normalizedZip);

      setDistanceOrigin(nextDistanceOrigin);
      setSortBy(currentValue => {
        if (currentValue === 'reviews') {
          return currentValue;
        }

        if (nextDistanceOrigin?.origin) {
          return 'distance';
        }

        return currentValue === 'distance' ? 'name' : currentValue;
      });

      if (!nextDistanceOrigin?.origin) {
        setSearchError(
          'We found stores for that ZIP, but could not calculate distance from your saved address, current location, or ZIP.'
        );
      }
    } finally {
      setSearchInProgress(false);
    }
  };

  return (
    <Page
      className={css.root}
      scrollingDisabled={scrollingDisabled}
      contentType="website"
      description="Find approved dealers, browse their store pages, and preview mapped dealer locations."
      title="Find A Dealer | BowTackle"
    >
      <TopbarContainer />

      <LayoutSingleColumn rootClassName={css.layoutRoot} mainColumnClassName={css.layoutMain}>
        <div className={css.content}>
          {dealersInProgress ? (
            <div className={css.loading}>
              Loading dealers...
            </div>
          ) : hasError ? (
            <div className={css.error}>
              We could not load dealers right now.
            </div>
          ) : hasDealers ? (
            <>
              <div className={css.splitLayout}>
                <div className={css.controlsColumn}>
                  <div className={css.hero}>
                    <Heading as="h1" className={css.title}>
                      Find Great Gear Nearby.
                    </Heading>
                    <p className={css.description}>
                      Search our network of trusted dealers to find the gear you need. Select a
                      dealer and browse their store page to learn more about what they offer.
                    </p>
                  </div>

                  <form className={css.searchBar} onSubmit={handleSearchSubmit}>
                    <input
                      className={css.searchInput}
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength="5"
                      placeholder="Enter ZIP Code"
                      value={draftZip}
                      onChange={event => {
                        const digitsOnly = event.target.value.replace(/\D/g, '').slice(0, 5);
                        setDraftZip(digitsOnly);
                      }}
                    />
                    <button type="submit" className={css.searchButton}>
                      {searchInProgress ? 'Searching...' : 'Search'}
                    </button>
                  </form>

                  {searchError ? <div className={css.searchError}>{searchError}</div> : null}
                </div>

                <div className={css.mapColumn}>
                  {mappedDealers.length > 0 ? (
                    <SearchMap
                      className={css.embeddedMapCanvas}
                      bounds={mapBounds}
                      center={activeSearchOrigin}
                      location={locationForMap}
                      listings={mapListings}
                      stores={mappedDealers}
                      activeListingId={activeMapListingId}
                      onMapMoveEnd={noop}
                      onActivateListing={listingId => setActiveDealerId(listingId?.uuid || null)}
                      reusableContainerClassName={css.embeddedMapContainer}
                      messages={intl.messages}
                      mapVariant="dealerLocator"
                    />
                  ) : (
                    <div className={css.mapEmptyState}>
                      We are geocoding dealer addresses where possible. Dealers without coordinates
                      still appear in the list.
                    </div>
                  )}
                </div>

                <div className={css.resultsColumn}>
                  <div className={css.resultsSummary}>
                    <span>{filteredDealers.length} dealers found</span>
                    {appliedZip ? <span>Showing stores in ZIP "{appliedZip}"</span> : null}
                    {searchInProgress && appliedZip ? <span>Calculating distance...</span> : null}
                    {appliedZip && activeSearchOrigin ? (
                      <span>{getDistanceOriginMessage(distanceOrigin?.source, appliedZip)}</span>
                    ) : null}
                  </div>

                  {filteredDealers.length > 0 ? (
                    <div className={css.dealersList}>
                      {filteredDealers.map(dealer => (
                        <DealerCard
                          key={dealer.id.uuid}
                          dealer={dealer}
                          distanceMiles={distanceByDealerId[dealer.id.uuid]}
                          isDistancePending={searchInProgress && !!appliedZip}
                          intl={intl}
                          isActive={dealer.id.uuid === activeDealerId}
                          onSelect={() => setActiveDealerId(dealer.id.uuid)}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className={css.noDealers}>
                      No dealers matched your search. Try a different ZIP code or clear the
                      filters.
                    </div>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className={css.noDealers}>
              No dealers are available yet.
            </div>
          )}
        </div>
      </LayoutSingleColumn>

      <FooterContainer />
    </Page>
  );
};

DealersDirectoryPageComponent.defaultProps = {
  currentUser: null,
  dealers: [],
  dealersInProgress: false,
  dealersError: null,
};

DealersDirectoryPageComponent.propTypes = {
  currentUser: propTypes.currentUser,
  dealers: arrayOf(propTypes.user),
  dealersInProgress: bool,
  dealersError: propTypes.error,
  intl: intlShape.isRequired,
  location: shape({
    search: string,
  }),
  scrollingDisabled: bool.isRequired,
  onManageDisableScrolling: func.isRequired,
};

const mapStateToProps = state => {
  return {
    currentUser: state.user.currentUser,
    dealers: selectDealers(state),
    dealersInProgress: selectDealersInProgress(state),
    dealersError: selectDealersError(state),
    scrollingDisabled: isScrollingDisabled(state),
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
});

const DealersDirectoryPage = compose(
  connect(mapStateToProps, mapDispatchToProps),
  injectIntl,
  withViewport
)(DealersDirectoryPageComponent);

DealersDirectoryPage.loadData = loadData;

export default DealersDirectoryPage;
